"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FaArrowRight,
  FaCalendarDays,
  FaPlay,
  FaPause,
  FaExpand,
  FaCompress,
  FaGift,
  FaCreditCard,
} from "react-icons/fa6";

import VideoVolumeButton from "./VideoVolumeButton";
import StoreBadges from "./StoreBadges";
import { HeroDecor } from "./HeroDecor";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { useLocale } from "@/app/providers/IntlProvider";
import { DEMO_URL } from "@/lib/demo";

/**
 * Launch film, one cut per language (tools/motion-video `flots` / `flots-en`, with sound).
 * `teaser` is what phones show instead of the whole 16:9 frame, where nothing could be read:
 * a square crop of the poster frame (the day's task list, t = 4 s), rendered at 2× from the
 * film's own scene (`render.mjs flots --stills 4 --scale 2`, crop 960×960 at 976,380).
 */
const HERO_VIDEO = {
  fr: { src: "/video/flots-fr.mp4", poster: "/video/flots-fr-poster.webp", teaser: "/video/flots-fr-teaser.webp" },
  en: { src: "/video/flots-en.mp4", poster: "/video/flots-en-poster.webp", teaser: "/video/flots-en-teaser.webp" },
} as const;

/** Phones: below `sm`, where the film shows as a readable crop and plays full screen. */
const PHONE_QUERY = "(max-width: 639.98px)";

/** 1×1 transparent GIF: the teaser's `<source>` from `sm`, so wider screens never download it. */
const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Vendor-prefixed full-screen API of iPhone Safari, which has no element full screen. */
type WebkitVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

/** `lock()` is missing from recent DOM typings but still shipped by Android browsers. */
type LockableOrientation = ScreenOrientation & { lock?: (o: "landscape") => Promise<void>; unlock?: () => void };

/** Film length as `m:ss` (the phone play button says how long the visitor commits to). */
const formatDuration = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

const GESTURES = ["pointerdown", "keydown"] as const;

/**
 * Keys that only move around the page (focus, scroll, modifiers, Escape): they are not an
 * intent to watch, and starting a film with sound on them would talk over a screen reader.
 */
const NAVIGATION_KEYS = new Set([
  "Tab", "Shift", "Control", "Alt", "Meta", "Escape", "CapsLock",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End",
]);

/** Seek step of the progress slider's arrow keys, in seconds. */
const SEEK_STEP = 5;

/**
 * The launch film in the hero, self-hosted per language.
 *
 * Product rule kept from the previous player: never a silent muted autoplay. The film
 * starts WITH sound on a click on it, or on the visitor's first gesture anywhere while
 * the film is on screen (browsers only grant audio on a real gesture). It pauses when
 * scrolled away or when the tab is hidden, and resumes on return unless the visitor
 * paused it. A touch never counts as that gesture: on a touch screen it is how the visitor
 * scrolls, not an intent to watch.
 *
 * Phones get their own shape: a square, readable crop of the film's frame with a play
 * button, and a labelled play button under it. The film then plays full screen (the native
 * player on iPhone, turned sideways where Android allows it) and folds back to the crop on
 * exit: a 16:9 film at 360 px wide carries no readable text.
 */
function HeroVideo() {
  const intl = useIntl();
  const { locale } = useLocale();
  const { src, poster, teaser } = HERO_VIDEO[locale];
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(true);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [started, setStarted] = useState(false);
  // Visitor intent (should the film be playing) vs. why it is paused right now.
  const wantsPlayRef = useRef(false);
  const startedRef = useRef(false);
  const visibleRef = useRef(false);

  const play = useCallback(async () => {
    const v = videoRef.current;
    if (!v) return false;
    try {
      await v.play();
      wantsPlayRef.current = true;
      if (!startedRef.current) trackEvent("hero-video-play", { locale }, {});
      startedRef.current = true;
      setStarted(true);
      return true;
    } catch {
      // No audio permission yet: stay paused with the play button visible.
      return false;
    }
  }, [locale]);

  const togglePause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      void play();
    } else {
      wantsPlayRef.current = false;
      v.pause();
    }
  };

  // First real gesture anywhere starts the film with sound, but only while it is on screen.
  // Gestures on the player itself are left to its own controls: otherwise the pointerdown
  // would start the film and the click on the play button would pause it right away.
  useEffect(() => {
    const onGesture = (e: Event) => {
      if (startedRef.current) return detach();
      if (!visibleRef.current) return;
      if (e instanceof KeyboardEvent && NAVIGATION_KEYS.has(e.key)) return;
      if (e instanceof PointerEvent && e.pointerType === "touch") return;
      // Reduced motion: the film waits for an explicit play.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return detach();
      if (e.target instanceof Node && containerRef.current?.contains(e.target)) return;
      void play().then((ok) => ok && detach());
    };
    const detach = () => GESTURES.forEach((e) => document.removeEventListener(e, onGesture));
    GESTURES.forEach((e) => document.addEventListener(e, onGesture, { passive: true }));
    return detach;
  }, [play]);

  // Pause when scrolled away, resume on return if the visitor had it playing.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        visibleRef.current = e.intersectionRatio >= 0.5;
        const v = videoRef.current;
        if (!v) return;
        if (e.intersectionRatio < 0.2 && !v.paused) v.pause();
        else if (visibleRef.current && v.paused && wantsPlayRef.current && !document.hidden) void v.play().catch(() => {});
      },
      { threshold: [0, 0.2, 0.5, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Pause with the tab, resume with it.
  useEffect(() => {
    const onVisibility = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else if (wantsPlayRef.current && visibleRef.current) void v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Leaving full screen on a phone folds the film back to its crop: stop it there, since
  // a film playing on under the crop could not be seen, only heard.
  useEffect(() => {
    const v = videoRef.current as WebkitVideo | null;
    const stopOnPhone = () => {
      if (!v || !window.matchMedia(PHONE_QUERY).matches) return;
      wantsPlayRef.current = false;
      v.pause();
      v.setAttribute("playsinline", "");
      (screen.orientation as LockableOrientation | undefined)?.unlock?.();
    };
    const onFs = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) stopOnPhone();
    };
    document.addEventListener("fullscreenchange", onFs);
    // iPhone's native player has no fullscreenchange, only this event on the video.
    v?.addEventListener("webkitendfullscreen", stopOnPhone);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      v?.removeEventListener("webkitendfullscreen", stopOnPhone);
    };
  }, [src]);

  // Switching language swaps the film: start over, paused.
  useEffect(() => {
    wantsPlayRef.current = false;
    startedRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset UI when the source changes
    setProgress(0);
    setStarted(false);
  }, [src]);

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = v.duration * pct;
  };

  /** Keyboard seeking on the progress slider (WAI-ARIA slider pattern). */
  const handleSeekKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const to: Record<string, number> = {
      ArrowRight: v.currentTime + SEEK_STEP,
      ArrowUp: v.currentTime + SEEK_STEP,
      ArrowLeft: v.currentTime - SEEK_STEP,
      ArrowDown: v.currentTime - SEEK_STEP,
      Home: 0,
      End: v.duration,
    };
    if (!(e.key in to)) return;
    e.preventDefault();
    v.currentTime = Math.max(0, Math.min(v.duration, to[e.key]));
  };

  const handleMuteToggle = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
  };

  const handleVolumeChange = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    v.muted = value === 0;
  };

  /**
   * Full screen on `el` where the Fullscreen API exists; otherwise (iPhone) the video's own
   * native player. Android turns the picture sideways once full screen, where locking the
   * orientation is allowed.
   */
  const enterFullscreen = (el: HTMLElement | null) => {
    const v = videoRef.current as WebkitVideo | null;
    if (el && document.fullscreenEnabled && el.requestFullscreen) {
      el.requestFullscreen()
        .then(() => (screen.orientation as LockableOrientation | undefined)?.lock?.("landscape"))
        .catch(() => {});
    } else {
      try {
        v?.webkitEnterFullscreen?.();
      } catch {
        // Not ready yet: the film still plays inline.
      }
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else enterFullscreen(containerRef.current);
  };

  /**
   * The film's play action. On phones it opens the film full screen: the video element
   * itself (native controls) where the Fullscreen API exists, and on iPhone, where it does
   * not, dropping `playsinline` makes Safari play it in its full-screen player. Elsewhere it
   * toggles playback in place, as before.
   */
  const playFromTeaser = () => {
    const v = videoRef.current;
    if (!v || !window.matchMedia(PHONE_QUERY).matches) return togglePause();
    if (!v.paused) return togglePause();
    if (document.fullscreenEnabled) enterFullscreen(v);
    else v.removeAttribute("playsinline");
    void play();
  };

  return (
    <>
    <div
      ref={containerRef}
      className={`group relative overflow-hidden bg-blue-light ${
        isFullscreen
          ? "flex items-center justify-center bg-black"
          : "rounded-[28px] shadow-[0_40px_90px_-40px_rgba(32,39,103,0.45)] ring-1 ring-blue-light max-sm:aspect-square sm:rounded-[36px]"
      }`}
    >
      <video
        ref={videoRef}
        key={src}
        src={src}
        poster={poster}
        preload="metadata"
        playsInline
        loop
        className={
          isFullscreen
            ? "max-h-full w-full"
            : "block aspect-video w-full max-sm:absolute max-sm:inset-0 max-sm:aspect-auto max-sm:h-full max-sm:object-contain"
        }
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (v.duration) setProgress((v.currentTime / v.duration) * 100);
        }}
        onVolumeChange={(e) => {
          setMuted(e.currentTarget.muted);
          setVolume(e.currentTarget.volume);
        }}
      />

      {/* Phones: the readable crop over the film while it is not playing. A <picture> whose
          `sm` source is a blank pixel, so wider screens never download it. */}
      {!isFullscreen && (
        <picture
          className={`pointer-events-none absolute inset-0 transition-opacity duration-300 sm:hidden ${paused ? "opacity-100" : "opacity-0"}`}
        >
          <source media="(min-width: 640px)" srcSet={BLANK_IMAGE} />
          {/* Decorative (empty alt): the labelled play button under the film names it. */}
          <img src={teaser} alt="" width={960} height={960} decoding="async" className="h-full w-full object-cover" />
        </picture>
      )}

      {/* Whole-film click target (mouse convenience); the labelled control is in the bar,
          and on phones under the film. */}
      <button
        type="button"
        onClick={playFromTeaser}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 z-5 flex cursor-pointer items-center justify-center"
      >
        {paused && (
          <>
            <span className="flex items-center gap-3 rounded-full bg-blue-main py-3 pl-3 pr-6 text-base font-semibold text-white shadow-lg shadow-blue-main/40 transition-transform duration-200 hover:scale-105 max-sm:hidden sm:text-lg">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 sm:h-12 sm:w-12">
                <FaPlay className="ml-0.5 text-sm sm:text-base" />
              </span>
              <FormattedMessage id="hero.video.play" />
            </span>
            {/* Phones: a plain play disc, low on the crop so the list above it stays readable. */}
            <span className="absolute bottom-5 right-5 flex h-14 w-14 items-center justify-center rounded-full bg-blue-main text-white shadow-lg shadow-blue-main/40 ring-4 ring-white/70 sm:hidden">
              <FaPlay className="ml-1 text-lg" />
            </span>
          </>
        )}
      </button>

      {/* Hidden until the film starts, but shown as soon as a control takes keyboard focus.
          On phones it only shows while the film plays inline (browsers without full screen). */}
      <div
        className={`absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 bg-linear-to-t from-black/60 to-transparent px-3 py-2.5 transition-opacity duration-300 sm:gap-3 sm:px-4 sm:py-3 ${
          paused && !started ? "opacity-0 focus-within:opacity-100" : "opacity-100"
        } ${paused ? "max-sm:hidden" : ""}`}
      >
        <button
          type="button"
          onClick={togglePause}
          aria-label={intl.formatMessage({ id: paused ? "a11y.video.resume" : "a11y.video.pause" })}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-main text-white transition-all hover:scale-110 hover:bg-blue-fill pointer-coarse:h-11 pointer-coarse:w-11 sm:h-8 sm:w-8 sm:pointer-coarse:h-11 sm:pointer-coarse:w-11"
        >
          {paused ? <FaPlay aria-hidden="true" className="ml-0.5 text-xs" /> : <FaPause aria-hidden="true" className="text-xs" />}
        </button>
        {/* Seek slider: the thin bar sits in a taller hit area so it stays easy to grab. */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={intl.formatMessage({ id: "a11y.video.progress" })}
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${Math.round(progress)} %`}
          onClick={handleSeek}
          onKeyDown={handleSeekKey}
          className="group/seek flex h-8 flex-1 cursor-pointer items-center rounded-full"
        >
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/30 transition-all duration-150 group-hover/seek:h-2.5">
            <div className="h-full rounded-full bg-white" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <VideoVolumeButton muted={muted} volume={volume} onMuteToggle={handleMuteToggle} onVolumeChange={handleVolumeChange} />
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={intl.formatMessage({ id: isFullscreen ? "a11y.video.exitFullscreen" : "a11y.video.enterFullscreen" })}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/90 transition-all hover:scale-110 hover:text-white pointer-coarse:h-11 pointer-coarse:w-11"
        >
          {isFullscreen ? <FaCompress aria-hidden="true" className="text-sm" /> : <FaExpand aria-hidden="true" className="text-sm" />}
        </button>
      </div>
    </div>

    {/* Phones: the labelled play control, under the crop rather than over it. */}
    <button
      type="button"
      onClick={playFromTeaser}
      className="mx-auto mt-4 flex min-h-11 cursor-pointer items-center gap-2.5 rounded-full bg-surface py-1.5 pl-1.5 pr-5 text-[15px] font-semibold text-title-blue shadow-md shadow-blue-main/10 ring-1 ring-blue-light sm:hidden"
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-main text-white">
        {paused ? <FaPlay aria-hidden="true" className="ml-0.5 text-xs" /> : <FaPause aria-hidden="true" className="text-xs" />}
      </span>
      <FormattedMessage id="hero.video.play" />
      {duration > 0 && <span className="font-medium tabular-nums text-gray-main">{formatDuration(duration)}</span>}
    </button>
    </>
  );
}

/**
 * Home hero: the promise (from the sales argument), the factual sub-line, the two CTAs
 * with the risk-reversal line, then the launch film and the store links.
 */
export default function HeroSection() {
  const { href: appUrl, target: appTarget } = useAppLink("hero");
  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden"
      style={{ background: "var(--grad-hero)" }}
    >
      <HeroDecor />
      {/* Below lg the app bar is 56 px tall: the headline starts 40 px under it, not 100.
          Phones: 34 px type (30 px under 360) sets the promise on three lines at 390, not five;
          a phone held sideways (short and not desktop-wide) gets 48 px, not 60. */}
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-6 pb-12 pt-24 text-center sm:pt-28 lg:pt-40">
        <h1 className="text-balance text-[1.875rem] font-bold leading-[1.06] tracking-[-0.03em] text-title-blue min-[360px]:text-[2.125rem] sm:text-6xl xl:text-7xl [@media(max-height:500px)_and_(max-width:1023.98px)]:text-5xl">
          <FormattedMessage
            id="hero.promise"
            values={{
              em: (chunks) => (
                <span key="em" className="text-brand">
                  {chunks}
                </span>
              ),
            }}
          />
        </h1>
        <p className="mt-6 max-w-2xl text-balance text-lg leading-[1.6] text-text-main sm:mt-7 sm:text-xl">
          <FormattedMessage id="hero.subhead" />
        </p>

        <div className="mt-8 flex w-full flex-col items-center gap-4 sm:mt-10">
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Link
              href={appUrl}
              // The mobile app bar shows its own sign-up pill once this one scrolls away.
              data-hero-cta=""
              onClick={() => {
                trackEvent("hero-cta-button", {}, { criticalOnNav: true });
                trackCtaClick("hero", appTarget);
              }}
              className="lift-hit group/cta flex items-center justify-center gap-2 rounded-full bg-blue-main px-5 py-4 text-base font-semibold text-white min-[360px]:px-8 min-[360px]:text-[17px] shadow-lg shadow-blue-main/30 transition duration-[400ms] ease-in-out hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-main/40"
            >
              <FormattedMessage id="hero.ctaPrimary" />
              <FaArrowRight aria-hidden="true" className="transition-transform duration-200 group-hover/cta:translate-x-1" />
            </Link>
            {/* Straight to the booking calendar, in a new tab so the page stays open. */}
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("hero-demo-button", {}, { criticalOnNav: true });
                trackCtaClick("hero", "demo");
              }}
              className="lift-hit group/demo flex items-center justify-center gap-2 rounded-full bg-surface px-5 py-4 text-base font-semibold text-brand min-[360px]:px-8 min-[360px]:text-[17px] ring-1 ring-blue-sec transition duration-[400ms] ease-in-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-main/15 hover:ring-blue-main"
            >
              <FormattedMessage id="hero.ctaSecondary" />
              <FaCalendarDays aria-hidden="true" className="transition-transform duration-200 group-hover/demo:-rotate-6" />
            </a>
          </div>
          {/* Text-safe brand blue: the hero wash under this line is tinted (4.0:1 with blue-main). */}
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-semibold text-brand-ink">
            <span className="inline-flex items-center gap-1.5">
              <FaGift aria-hidden="true" className="shrink-0" />
              <FormattedMessage id="hero.freeBadge" />
            </span>
            <span className="text-blue-sec" aria-hidden="true">
              ·
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FaCreditCard aria-hidden="true" className="shrink-0" />
              <FormattedMessage id="hero.noCard" />
            </span>
          </div>
        </div>
      </div>

      <div className="relative z-10 px-4 pb-12 sm:px-8">
        {/* Wider film on large screens; HeroDecor keeps its icons clear of these widths. */}
        <div className="mx-auto max-w-5xl min-[1920px]:max-w-6xl min-[2200px]:max-w-7xl">
          <HeroVideo />
        </div>
        <StoreBadges />
      </div>
    </section>
  );
}
